import Vue from 'vue';
import TokenService from '@/services/token.service';
import BlobConverter from '@/utils/blobconverter';
import { store, mutations } from '@/store/store';
import UserService from '@/services/user.service';
import ParticipantService from '@/services/participant.service';
import Axios from 'axios';
import router from '@/router';

declare module 'axios' {
  interface AxiosRequestConfig {
    handlerErrorEnabled: boolean;
    handlerSuccessEnabled: boolean;
  }
}

export default class ApiHandler extends Vue {

  private readonly TOKEN_EXPIRED = 'TOKEN_EXPIRED';
  private readonly NETWORK_ERROR = 'Network Error';

  public requestHandler(requestConfig: any) {
    if (this.isHandlerSuccessEnabled(requestConfig) || this.isHandlerErrorEnabled(requestConfig)) {
      // Check token expiration if the user is connected
      if ((store.state.isAuthenticated || store.state.isIdentified) && store.state.tokenTimestamp > 0) {
        const timestampNow = Math.ceil(Date.now() / 1000);
        if (store.state.tokenTimestamp <= timestampNow) {
          // token expired : redirect to login page
          if (store.state.isAuthenticated) {
            UserService.logoutFromFrontend();
            router.push({
              name: 'login',
            });
          } else if (store.state.isIdentified) {
            ParticipantService.logoutFromFrontend();
            router.push({
              name: 'login',
            });
          }

          const CancelToken = Axios.CancelToken;

          // Cancel the request
          return {
            ...requestConfig,
            cancelToken: new CancelToken((cancel) => cancel(this.TOKEN_EXPIRED)),
          };
        }
      }

      // set loading state to true
      // todo: maybe add navigation guards to prevent page changes before requests is done?
      // + apply these setLoading states to everywhere you need.
      this.setLoadingState(true);
    }
    return requestConfig;
  }

  public async errorHandler(error: any) {
    if (this.isHandlerErrorEnabled(error.config)) {
      // Handle errors

      // set loading state to false
      this.setLoadingState(false);

      let errorMessage;
      let showNotif = true;

      // Check any error (without response) first
      if (!error.response) {
        errorMessage = 'error';
        if (error.message === this.NETWORK_ERROR) {
          errorMessage = 'networkError';
        } else if (error.message === this.TOKEN_EXPIRED) {
          errorMessage = 'sessionExpiration';
        }
      } else {
        // Check error in response
        switch (error.response.status) {
          case (500):
            errorMessage = 'serverError';
            break;
          case (401):
            // In case someone was able to make a call to the API without a JWT
            mutations.setPopUpErrMessage(error.response.data.message);
            mutations.setShowErrInfoModal(true);
            if (!TokenService.tokenExists()) {
              errorMessage = 'sessionExpiration';
              if (store.state.isAuthenticated) {
                UserService.logoutFromFrontend();
                router.push({
                  name: 'login',
                });
              } else if (store.state.isIdentified) {
                ParticipantService.logoutFromFrontend();
                router.push({
                  name: 'login',
                });
              } else {
                errorMessage = error.response.data.message;
              }
            } else {
              // In case of a call to the API with a non valid JWT : get the error message received from the API
              errorMessage = error.response.data.message;
            }
            break;
          default:
            if (error.request.responseType === 'blob') {
              // In case the template is not found, don't show the notif error.
              if (error.response.status === 404) {
                showNotif = false;
              } else {
                const { data } = error.response;
                // Read file
                const file = await BlobConverter.fileReader(data);
                // Parse content and retrieve 'message'
                const { message } = JSON.parse(file as string);
                errorMessage = message;
              }
            } else {
              errorMessage = error.response.data.message;
            }
            break;
        }
      }

      if (showNotif) {
        this.notify(errorMessage);
      }
    }
    return Promise.reject({ ...error });
  }

  public isHandlerErrorEnabled(config = { handlerEnabled: {} }) {
    return config.hasOwnProperty('handlerErrorEnabled') && !config.handlerEnabled ?
      false : true;
  }

  public isHandlerSuccessEnabled(config = { handlerEnabled: {} }) {
    return config.hasOwnProperty('handlerSuccessEnabled') && !config.handlerEnabled ?
      false : true;
  }

  public successHandler(responseConfig: any) {
    if (this.isHandlerSuccessEnabled(responseConfig.config)) {
      // Handle sucesses
      // set loading state to false
      this.setLoadingState(false);
    }
    return responseConfig;
  }

  protected setLoadingState(value: boolean): void {
    mutations.setLoadingState(value);
  }

  private notify(text: string = 'error', type: string = 'error', title: string = 'error', duration: number = 20000) {
    this.$notify({
      type,
      title: `errorMgs.${title}`,
      text: `errorMgs.${text}`,
      duration,
    });
  }
}
